import { z } from "zod";

/**
 * Validated environment.
 *
 * Deliberately **not** vendored from `arrow-fe`: that file reads
 * `process.env.NEXT_PUBLIC_*`, which Next inlines by matching the literal text.
 * Vite inlines `import.meta.env.VITE_*` instead. Same idea, different mechanism,
 * so this is the one piece of the client that is genuinely rewritten rather than
 * copied.
 *
 * Everything here ships in the browser bundle and is readable by anyone. Never
 * put a secret in a `VITE_` variable.
 */

const schema = z.object({
  /**
   * The same `arrow-be` the storefront uses.
   *
   * In production this must resolve on the `arrowindia.com` registrable domain,
   * or the refresh cookie — `SameSite=Lax`, `Path=/v1/auth` — is simply not
   * sent and every session silently ends after 15 minutes. See ADMIN.md §2.
   */
  VITE_API_BASE_URL: z.url(),

  /**
   * Whether `apiFetch` seals its payloads.
   *
   * Left unset, this follows the build mode. That default matters less here
   * than on the storefront, because `arrow-be` currently runs
   * `API_ENCRYPTION=required` in development too — so local work needs it ON
   * regardless of mode.
   */
  VITE_API_ENCRYPTION: z.enum(["on", "off"]).optional(),

  /** Must match the API's own `GOOGLE_CLIENT_ID`, or `aud` verification fails. */
  VITE_GOOGLE_CLIENT_ID: z.string().min(1).optional(),
});

const parsed = schema.safeParse({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_API_ENCRYPTION: import.meta.env.VITE_API_ENCRYPTION,
  VITE_GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
});

if (!parsed.success) {
  const issues = parsed.error.issues
    .map((issue) => `  - ${issue.path.join(".") || "(root)"}: ${issue.message}`)
    .join("\n");

  throw new Error(`Invalid environment variables:\n${issues}\nCheck .env against .env.example.`);
}

/**
 * Named `env` with the same shape the vendored `api-client.ts` expects, so that
 * file needs no edits beyond its import. The two keys it reads are aliased
 * below rather than renamed at every call site.
 */
export const env = {
  NEXT_PUBLIC_API_BASE_URL: parsed.data.VITE_API_BASE_URL,
  VITE_GOOGLE_CLIENT_ID: parsed.data.VITE_GOOGLE_CLIENT_ID,
} as const;

export const googleClientId: string | undefined = parsed.data.VITE_GOOGLE_CLIENT_ID;

/**
 * Whether to encrypt API payloads.
 *
 * Derived rather than required, so a forgotten flag cannot be the reason a
 * production bundle ships readable payloads.
 */
export const encryptPayloads: boolean = parsed.data.VITE_API_ENCRYPTION
  ? parsed.data.VITE_API_ENCRYPTION === "on"
  : import.meta.env.PROD;
